import _ from "lodash";
import classNames from "classnames";
import Datasource, { DatasourceResponse } from "lib/datasource";
import moment from "moment";
import React, { memo, useEffect, useState } from "react";
import { EmptyState, LoadingState, View } from "components/Shared";
import { translate } from "lib";

/**
 * Every inbound /lease_data call that WROTE ONE AMENDMENT, newest first, with the
 * slice of the request that named it and the slice of the response TCP sent back
 * (`external_api_records`).
 *
 * Why it exists: `amendments.meta` only ever holds the LATEST payload, so "when
 * did Yardi send this, and what did we answer" had no answer. Failures are here
 * too — often the only reason anyone opens this tab.
 *
 * ---------------------------------------------------------------------------
 * ONE COMPONENT, TWO TABS, ONE SET OF ROWS (Sim, 27 Aug 2026)
 * ---------------------------------------------------------------------------
 *   the AMENDMENT page      "API Call"       V2::AmendmentApiCallsController
 *   the PROJECT workspace   "Amendment JSON" V2::ProjectAmendmentJsonController
 *
 * Sim: *"'Amendment JSON' tab [that] shows the exact same thing [as] the 'API
 * Call' tab in the Amendment. Since Project belongs to Amendment, so we can just
 * take it from its amendment."* — which is what `Project#external_api_records`
 * (`through: :amendment`) is. Hence `path`: the two callers differ only in which
 * endpoint they read and whether the panel is `restricted`, so there is one
 * component rather than a lookalike.
 *
 * The endpoints are separate because their GATES are: the amendment page answers
 * to AmendmentPolicy#show? behind the `lease-agreements` flag, while a project
 * workspace is shared with the tenant and their contractors and so is
 * administrator-only (ProjectPolicy#amendment_json?). See
 * V2::ProjectAmendmentJsonController.
 *
 * `restricted` is the padlock: OFF on the amendment page, where this tab is gated
 * exactly as the Details and Assets tabs beside it and a padlock would claim a
 * restriction that is not there; ON in the project workspace, where it is a real
 * one.
 *
 * What is NOT here either way, by design: a call that changed nothing ("skipped"),
 * and a failure naming a TCode + AmendmentID TCP has never seen (no amendment to
 * file it under). Both decisions are Sim's; see ExternalApiRecorder.
 */
interface Props {
	// The v2 path to read, e.g. `amendments/:id/api-calls` or
	// `projects/:id/amendment-json`. Both answer with `external_api_record` rows.
	path: string;
	restricted?: boolean;
	name?: string;
	icon?: string;
}

const formatDateTime = (value?: string | null) => (!!value ? moment(value).format("D MMM YYYY, h:mm A") : "");

/**
 * Pretty-printed, and RAW: the point of the tab is reading exactly what was sent
 * and exactly what came back, so nothing here reshapes either document. `<pre>`
 * scrolls rather than wrapping, because a lease object is one long line of JSON
 * and re-flowing it destroys the nesting that makes it readable.
 */
const Payload = ({ label, document }: { label: string; document: any }) => (
	<div style={{ marginBottom: "0.75rem" }}>
		<strong>{label}</strong>
		<pre
			style={{
				margin: "0.25rem 0 0",
				padding: "0.5rem",
				maxHeight: "20rem",
				overflow: "auto",
				background: "#f7f7f9",
				fontSize: "0.75rem"
			}}
		>
			{JSON.stringify(document || {}, null, 2)}
		</pre>
	</div>
);

const AmendmentApiCallsTab = ({ path, restricted }: Props) => {
	// null = still loading; an empty array is a legitimate answer (an amendment
	// created by the xlsx importer has no API history at all — imports are
	// deliberately not recorded — and a project made inside TCP has no amendment).
	const [records, setRecords] = useState<any[] | null>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "external_api_record" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setRecords(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(path)) datasource.get("v2", path, {});
	}, [datasource, path]);

	if (records === null) return <LoadingState />;

	// The shared empty-state box, like the Assets tab beside it — but with its own
	// sentence rather than `resource="api_calls"`, which would render "No API Call
	// found".
	if (_.isEmpty(records))
		return (
			<View restricted={restricted}>
				<EmptyState>
					<div>{translate("no_api_calls")}</div>
				</EmptyState>
			</View>
		);

	return (
		<View restricted={restricted}>
			<table className="table" style={{ width: "100%", tableLayout: "fixed" }}>
				<colgroup>
					<col style={{ width: "20%" }} />
					<col style={{ width: "15%" }} />
					<col style={{ width: "65%" }} />
				</colgroup>
				<thead>
					<tr>
						<th>{translate("api_call_received")}</th>
						<th>{translate("api_call_outcome")}</th>
						<th>{translate("api_call_payload")}</th>
					</tr>
				</thead>
				<tbody>
					{_.map(records, record => (
						// A failed call is the row someone came here to find, so it is red —
						// the same signal the Milestone Data tab uses for missing data.
						<tr key={record.id} className={classNames({ "text-danger": record.success === false })}>
							<td>
								{formatDateTime(_.get(record, "requested_at"))}
								{/* A row recovered from projects.meta cannot say what TCP
								    answered or exactly when the call landed. Labelled rather
								    than dressed up as a complete record. */}
								{!!record.backfilled && (
									<div style={{ fontSize: "0.75rem", fontStyle: "italic" }} title={translate("api_call_backfilled")}>
										{translate("api_call_backfilled")}
									</div>
								)}
							</td>
							<td>
								<strong>{_.get(record, "outcome")}</strong>
								{!_.isEmpty(_.get(record, "error_messages")) && (
									<ul style={{ margin: "0.25rem 0 0", paddingLeft: "1rem", fontSize: "0.75rem" }}>
										{_.map(_.get(record, "error_messages"), (message, index) => (
											<li key={index}>{message}</li>
										))}
									</ul>
								)}
							</td>
							{/* Collapsed by default: a lease object runs to dozens of lines and
							    an amendment can be re-sent many times. <details> rather than
							    local state — no re-render, and the browser keeps it open while
							    the tab is mounted. */}
							<td>
								<details>
									<summary style={{ cursor: "pointer" }}>{translate("api_call_view_payload")}</summary>
									<div style={{ marginTop: "0.5rem" }}>
										<Payload label={translate("api_call_request")} document={_.get(record, "request_payload")} />
										<Payload label={translate("api_call_response")} document={_.get(record, "response_payload")} />
									</div>
								</details>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</View>
	);
};

export default memo(AmendmentApiCallsTab);
